import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, Sparkles, Eye, X, Check, Plus, Trash2, ChevronUp, ChevronDown,
  BookOpen, ArrowRight,
} from "lucide-react";
import { CREDENTIAL_ICON_OPTIONS, DEFAULT_CREDENTIAL_ICON, credentialIcon } from "../data/credentialIcons";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "digsby_admin_token";

function authHeader() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } };
}

const emptyForm = () => ({
  name: "", tags: [], headshot: "", headshotAlt: "", bio: [""],
  quote: "", quoteAttribution: "", credentialsHeading: "Credentials & Recognition",
  credentials: [], status: "published",
  seo: { title: "", metaDescription: "", imageAlt: "" },
});

function normalize(content) {
  const c = content || {};
  return {
    ...emptyForm(),
    ...c,
    tags: Array.isArray(c.tags) ? c.tags : [],
    bio: Array.isArray(c.bio) && c.bio.length ? c.bio : [""],
    credentials: Array.isArray(c.credentials) ? c.credentials : [],
    seo: { title: "", metaDescription: "", imageAlt: "", ...(c.seo || {}) },
  };
}

export default function AboutManager({ onAuthError }) {
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [diffs, setDiffs] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleErr = useCallback((err) => {
    if (err.response?.status === 401) { onAuthError(); return; }
    toast.error(err.response?.data?.detail || "Something went wrong.");
  }, [onAuthError]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/about`, authHeader());
      setHasDraft(!!data.draft);
      setForm(normalize(data.draft || data.published));
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  }, [handleErr]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));

  // Bio paragraphs
  const setBio = (i, v) => setForm((f) => { const b = [...f.bio]; b[i] = v; return { ...f, bio: b }; });
  const addBio = () => setForm((f) => ({ ...f, bio: [...f.bio, ""] }));
  const removeBio = (i) => setForm((f) => ({ ...f, bio: f.bio.filter((_, x) => x !== i) }));
  const moveBio = (i, d) => setForm((f) => {
    const b = [...f.bio]; const j = i + d;
    if (j < 0 || j >= b.length) return f;
    [b[i], b[j]] = [b[j], b[i]]; return { ...f, bio: b };
  });

  // Tags
  const setTag = (i, v) => setForm((f) => { const t = [...f.tags]; t[i] = v; return { ...f, tags: t }; });
  const addTag = () => setForm((f) => ({ ...f, tags: [...f.tags, ""] }));
  const removeTag = (i) => setForm((f) => ({ ...f, tags: f.tags.filter((_, x) => x !== i) }));

  // Credentials
  const setCred = (i, k, v) => setForm((f) => {
    const c = [...f.credentials]; c[i] = { ...c[i], [k]: v }; return { ...f, credentials: c };
  });
  const addCred = () => setForm((f) => ({ ...f, credentials: [...f.credentials, { icon: DEFAULT_CREDENTIAL_ICON, label: "" }] }));
  const removeCred = (i) => setForm((f) => ({ ...f, credentials: f.credentials.filter((_, x) => x !== i) }));
  const moveCred = (i, d) => setForm((f) => {
    const c = [...f.credentials]; const j = i + d;
    if (j < 0 || j >= c.length) return f;
    [c[i], c[j]] = [c[j], c[i]]; return { ...f, credentials: c };
  });

  const uploadHeadshot = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/admin/upload-cover`, fd, {
        headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" },
      });
      set("headshot", data.url);
      toast.success("Headshot uploaded.");
    } catch (err) { handleErr(err); } finally { setUploading(false); }
  };

  const runAnalyze = async () => {
    setAnalyzing(true);
    try {
      const payload = {
        name: form.name,
        tags: form.tags.map((t) => t.trim()).filter(Boolean),
        bio: form.bio.map((p) => p.trim()).filter(Boolean),
        quote: form.quote || "",
        seo: form.seo,
      };
      const { data } = await axios.post(`${API}/api/admin/about/analyze`, payload, authHeader());
      const acc = {};
      data.diffs.forEach((d) => { acc[d.field] = d.type !== "unchanged"; });
      setDiffs(data.diffs); setAccepted(acc);
    } catch (err) { handleErr(err); } finally { setAnalyzing(false); }
  };

  const applyDiffs = () => {
    const next = { ...form, seo: { ...form.seo } };
    diffs.forEach((d) => {
      if (!accepted[d.field]) return;
      if (d.field === "bio") next.bio = d.after.split("\n\n").map((s) => s.trim()).filter(Boolean);
      else if (d.field === "tags") next.tags = d.after.split(" · ").map((s) => s.trim()).filter(Boolean);
      else if (d.field === "quote") next.quote = d.after;
      else if (d.field.startsWith("seo.")) next.seo[d.field.split(".")[1]] = d.after;
    });
    if (!next.bio.length) next.bio = [""];
    setForm(next); setDiffs(null);
    toast.success("Suggestions applied. Review and save.");
  };

  const save = async (status) => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (status === "published" &&
        !window.confirm("Publish now? This updates the public About page for all visitors immediately.")) return;
    setSaving(true);
    const payload = {
      ...form,
      status,
      tags: form.tags.map((t) => t.trim()).filter(Boolean),
      bio: form.bio.map((p) => p.trim()).filter(Boolean),
      credentials: form.credentials
        .filter((c) => (c.label || "").trim())
        .map((c) => ({ icon: c.icon || DEFAULT_CREDENTIAL_ICON, label: c.label.trim() })),
    };
    try {
      await axios.put(`${API}/api/admin/about`, payload, authHeader());
      toast.success(status === "published" ? "About page published." : "Draft saved.");
      await load();
    } catch (err) { handleErr(err); } finally { setSaving(false); }
  };

  const inputCls = "w-full min-h-[46px] rounded-xl border-2 border-black/15 px-4 font-medium outline-none focus:border-[#0000FF]";

  if (loading) return <p className="text-slate-500 py-16 text-center">Loading…</p>;

  return (
    <div className="max-w-3xl" data-testid="about-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl">About Page</h1>
          <p className="text-slate-600">Edit the public About the Author page.</p>
        </div>
        {hasDraft && (
          <span data-testid="about-draft-badge" className="rounded-full bg-[#FBEF14] text-black text-xs font-bold px-3 py-1.5">
            Unpublished draft
          </span>
        )}
      </div>

      <div className="space-y-6 bg-white rounded-3xl border-2 border-black/10 p-6 sm:p-8">
        {/* Headshot */}
        <Field label="Headshot image">
          <div className="flex items-center gap-4">
            <div className="w-20 h-24 rounded-lg overflow-hidden bg-[#FFFBF2] border-2 border-black/10 flex-shrink-0">
              {form.headshot ? <img src={form.headshot} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <label className="inline-flex items-center gap-2 rounded-full border-2 border-black/15 px-4 py-2 font-bold cursor-pointer hover:bg-[#FBEF14]">
              <Upload size={16} /> {uploading ? "Uploading…" : "Upload headshot"}
              <input type="file" accept="image/*" className="hidden" data-testid="about-upload" onChange={(e) => uploadHeadshot(e.target.files[0])} />
            </label>
          </div>
        </Field>
        <Field label="Headshot alt text">
          <input className={inputCls} data-testid="about-headshot-alt" value={form.headshotAlt} onChange={(e) => set("headshotAlt", e.target.value)} />
        </Field>

        <Field label="Name *">
          <input className={inputCls} data-testid="about-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </Field>

        {/* Tags */}
        <Field label="Descriptor tags (shown as Educator · Researcher · …)">
          <div className="space-y-2">
            {form.tags.map((t, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls} data-testid={`about-tag-${i}`} value={t} onChange={(e) => setTag(i, e.target.value)} placeholder={`Tag ${i + 1}`} />
                <button onClick={() => removeTag(i)} data-testid={`about-tag-remove-${i}`} className="rounded-xl border-2 border-black/15 px-3 hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            ))}
            <button onClick={addTag} data-testid="about-tag-add" className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]"><Plus size={15} /> Add tag</button>
          </div>
        </Field>

        {/* Bio */}
        <Field label="Biography paragraphs">
          <div className="space-y-3">
            {form.bio.map((p, i) => (
              <div key={i} className="rounded-2xl border-2 border-black/10 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-slate-500">Paragraph {i + 1}</span>
                  <div className="flex gap-1">
                    <button onClick={() => moveBio(i, -1)} className="p-1.5 rounded-lg hover:bg-black/5 disabled:opacity-30" disabled={i === 0}><ChevronUp size={15} /></button>
                    <button onClick={() => moveBio(i, 1)} className="p-1.5 rounded-lg hover:bg-black/5 disabled:opacity-30" disabled={i === form.bio.length - 1}><ChevronDown size={15} /></button>
                    <button onClick={() => removeBio(i)} data-testid={`about-bio-remove-${i}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 disabled:opacity-30" disabled={form.bio.length === 1}><Trash2 size={15} /></button>
                  </div>
                </div>
                <textarea className={`${inputCls} py-3 resize-y`} rows={4} data-testid={`about-bio-${i}`} value={p} onChange={(e) => setBio(i, e.target.value)} />
              </div>
            ))}
            <button onClick={addBio} data-testid="about-bio-add" className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]"><Plus size={15} /> Add paragraph</button>
          </div>
        </Field>

        {/* Quote */}
        <Field label="Pull-quote">
          <textarea className={`${inputCls} py-3 resize-y`} rows={2} data-testid="about-quote" value={form.quote} onChange={(e) => set("quote", e.target.value)} />
        </Field>
        <Field label="Pull-quote attribution">
          <input className={inputCls} data-testid="about-quote-attr" value={form.quoteAttribution} onChange={(e) => set("quoteAttribution", e.target.value)} />
        </Field>

        {/* Credentials */}
        <Field label="Credentials heading">
          <input className={inputCls} data-testid="about-cred-heading" value={form.credentialsHeading} onChange={(e) => set("credentialsHeading", e.target.value)} />
        </Field>
        <Field label="Credentials & Recognition items">
          <div className="space-y-2">
            {form.credentials.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select className="min-h-[46px] rounded-xl border-2 border-black/15 px-3 font-medium outline-none focus:border-[#0000FF]" data-testid={`about-cred-icon-${i}`} value={c.icon || DEFAULT_CREDENTIAL_ICON} onChange={(e) => setCred(i, "icon", e.target.value)}>
                  {CREDENTIAL_ICON_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <input className={inputCls} data-testid={`about-cred-label-${i}`} value={c.label} onChange={(e) => setCred(i, "label", e.target.value)} placeholder="Credential text" />
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => moveCred(i, -1)} className="p-2 rounded-lg border-2 border-black/15 hover:bg-black/5 disabled:opacity-30" disabled={i === 0}><ChevronUp size={15} /></button>
                  <button onClick={() => moveCred(i, 1)} className="p-2 rounded-lg border-2 border-black/15 hover:bg-black/5 disabled:opacity-30" disabled={i === form.credentials.length - 1}><ChevronDown size={15} /></button>
                  <button onClick={() => removeCred(i)} data-testid={`about-cred-remove-${i}`} className="p-2 rounded-lg border-2 border-black/15 hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            <button onClick={addCred} data-testid="about-cred-add" className="inline-flex items-center gap-1 rounded-full border-2 border-black/15 px-4 py-2 text-sm font-bold hover:bg-[#FBEF14]"><Plus size={15} /> Add credential</button>
          </div>
        </Field>

        {/* AI */}
        <div className="rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-4">
          <p className="font-heading font-bold text-sm mb-1">AI spelling &amp; grammar check</p>
          <p className="text-sm text-slate-600 mb-3">Fixes typos and removes dashes without rewriting the bio. Nothing changes until you accept it.</p>
          <button onClick={runAnalyze} disabled={analyzing} data-testid="about-analyze"
            className="inline-flex items-center gap-2 min-h-[46px] rounded-full bg-[#00A000] text-white px-5 font-bold hover:bg-[#008800] disabled:opacity-60">
            <Sparkles size={16} /> {analyzing ? "Analyzing…" : "Analyze & Review"}
          </button>
        </div>

        {/* SEO */}
        <div className="border-2 border-black/10 rounded-2xl p-4 space-y-4">
          <p className="font-heading font-bold">SEO &amp; social</p>
          <Field label="SEO title"><input className={inputCls} data-testid="about-seo-title" value={form.seo.title} onChange={(e) => setSeo("title", e.target.value)} /></Field>
          <Field label="Meta description"><textarea className={`${inputCls} py-3`} rows={2} data-testid="about-seo-meta" value={form.seo.metaDescription} onChange={(e) => setSeo("metaDescription", e.target.value)} /></Field>
          <Field label="Image alt text (OG image)"><input className={inputCls} data-testid="about-seo-alt" value={form.seo.imageAlt} onChange={(e) => setSeo("imageAlt", e.target.value)} /></Field>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button onClick={() => setPreviewOpen(true)} data-testid="about-preview"
            className="inline-flex items-center gap-2 min-h-[48px] rounded-full border-2 border-black/15 px-5 font-bold hover:bg-[#FBEF14]"><Eye size={16} /> Preview</button>
          <button onClick={() => save("published")} disabled={saving} data-testid="about-publish"
            className="inline-flex items-center min-h-[48px] rounded-full bg-[#0000FF] text-white px-6 font-bold hover:bg-[#0000d6] disabled:opacity-60">Publish Now</button>
          <button onClick={() => save("draft")} disabled={saving} data-testid="about-save-draft"
            className="inline-flex items-center min-h-[48px] rounded-full border-2 border-black px-6 font-bold hover:bg-[#FBEF14] disabled:opacity-60">Save as Draft</button>
        </div>
      </div>

      {diffs && (
        <AboutDiffReview diffs={diffs} accepted={accepted} setAccepted={setAccepted}
          onApply={applyDiffs} onClose={() => setDiffs(null)} />
      )}
      {previewOpen && <AboutPreview form={form} onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}

function AboutDiffReview({ diffs, accepted, setAccepted, onApply, onClose }) {
  const TYPE_BADGE = { generated: "bg-[#00EE00] text-black", revised: "bg-[#FBEF14] text-black", unchanged: "bg-slate-200 text-slate-600" };
  const changed = diffs.filter((d) => d.type !== "unchanged");
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="about-diff-review">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Sparkles size={20} className="text-[#00A000]" /> Review AI Suggestions</h2>
          <button onClick={onClose} data-testid="about-diff-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>
        <p className="text-slate-600 mb-5">Nothing is applied until you accept it. {changed.length} suggested change{changed.length === 1 ? "" : "s"}.</p>
        <div className="space-y-4">
          {diffs.map((d) => (
            <div key={d.field} className={`rounded-2xl border-2 p-4 ${d.type === "unchanged" ? "border-black/5 opacity-60" : "border-black/10"}`} data-testid={`about-diff-${d.field}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="font-heading font-bold">{d.label}</span>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TYPE_BADGE[d.type]}`}>{d.type}</span>
                  {d.type !== "unchanged" && (
                    <label className="flex items-center gap-1 text-sm font-bold cursor-pointer">
                      <input type="checkbox" data-testid={`about-diff-accept-${d.field}`} checked={!!accepted[d.field]}
                        onChange={(e) => setAccepted((a) => ({ ...a, [d.field]: e.target.checked }))} className="w-4 h-4" /> Accept
                    </label>
                  )}
                </div>
              </div>
              {d.type !== "unchanged" && (
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-xl bg-red-50 border border-red-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-red-400 mb-1">BEFORE</span>{d.before || <em className="text-slate-400">(empty)</em>}</div>
                  <div className="rounded-xl bg-green-50 border border-green-100 p-3 whitespace-pre-line"><span className="block text-xs font-bold text-green-500 mb-1">AFTER</span>{d.after}</div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onApply} data-testid="about-diff-apply" className="inline-flex items-center gap-2 min-h-[48px] rounded-full bg-[#00A000] text-white px-6 font-bold hover:bg-[#008800]"><Check size={18} /> Apply Selected</button>
          <button onClick={onClose} className="inline-flex items-center min-h-[48px] rounded-full border-2 border-black/15 px-6 font-bold hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AboutPreview({ form, onClose }) {
  const tagsLine = (form.tags || []).filter(Boolean).join(" · ");
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="about-preview-modal">
      <div className="bg-[#FFFBF2] rounded-3xl max-w-5xl w-full my-8 p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><Eye size={20} /> Live Preview</h2>
          <button onClick={onClose} data-testid="about-preview-close" className="p-2 rounded-full hover:bg-black/5"><X size={22} /></button>
        </div>

        <section className="grid gap-10 lg:grid-cols-[300px_1fr] items-start">
          <div className="relative">
            <div className="absolute inset-0 rotate-3 rounded-[2rem] bg-[#0000FF]/10" aria-hidden />
            {form.headshot && (
              <img src={form.headshot} alt={form.headshotAlt} className="relative rounded-[2rem] border-4 border-black shadow-[8px_8px_0px_#FBEF14] w-full object-cover aspect-[4/5]" />
            )}
          </div>
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FF0000] uppercase tracking-wider"><BookOpen size={16} /> About the Author</span>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-3 text-black leading-tight">{form.name}</h1>
            {tagsLine && <p className="mt-2 font-heading font-bold text-lg text-[#0000FF]">{tagsLine}</p>}
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              {form.bio.filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
            </div>
            {form.quote && (
              <blockquote className="mt-8 relative rounded-[2rem] bg-black text-white p-8">
                <div className="absolute -top-5 left-8 text-6xl font-heading text-[#FBEF14] leading-none" aria-hidden>&ldquo;</div>
                <p className="font-heading font-bold text-xl leading-snug">{form.quote}</p>
                {form.quoteAttribution && <footer className="mt-4 text-slate-300 font-medium">{form.quoteAttribution}</footer>}
              </blockquote>
            )}
          </div>
        </section>

        {form.credentials.filter((c) => c.label).length > 0 && (
          <section className="mt-10 bg-white border-2 border-black/10 rounded-3xl p-6">
            <h2 className="font-heading font-bold text-2xl text-center">{form.credentialsHeading}</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {form.credentials.filter((c) => c.label).map((c, i) => {
                const Icon = credentialIcon(c.icon);
                return (
                  <div key={i} className="flex items-center gap-4 rounded-2xl bg-[#FFFBF2] border-2 border-black/10 p-5">
                    <span className="flex-shrink-0 w-12 h-12 rounded-full bg-[#0000FF] flex items-center justify-center"><Icon size={22} className="text-white" /></span>
                    <span className="font-heading font-bold text-slate-800 leading-tight">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-heading font-bold mb-2 text-sm">{label}</label>
      {children}
    </div>
  );
}
